
import React, { useState } from 'react';
import sesImage from "../../images/ses.png";
import logoImage from "../../images/logo.svg";
import logoWhiteImage from "../../images/logo_white.svg";

export default function Home() {
  const [formData, setFormData] = useState({
    type: '',
    company: '',
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  // 新しく追加: 送信結果が成功か否かを保持するステート
  const [submitSuccess, setSubmitSuccess] = useState<boolean | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');
    setSubmitSuccess(null); // 送信開始時はリセット

    try {
      const formDataToSend = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch(
        'https://readdy.ai/api/form/d3d08d1l9s1mk3m60it0',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formDataToSend,
        }
      );

      if (response.ok) {
        setSubmitStatus(
          'お問い合わせありがとうございます。担当者より折り返しご連絡いたします。'
        );
        setSubmitSuccess(true);
        setFormData({
          type: '',
          company: '',
          name: '',
          email: '',
          phone: '',
          message: '',
        });
      } else {
        setSubmitStatus('送信に失敗しました。もう一度お試しください。');
        setSubmitSuccess(false);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('送信に失敗しました。もう一度お試しください。');
      setSubmitSuccess(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <div
      className="min-h-screen bg-white"
      style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
    >
      {/* ナビゲーション */}
      <nav className="bg-white shadow-sm border-b border-gray-100 sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <a href="#">
                <img
                  src={logoImage}
                  alt="XROPOロゴ"
                  className="h-10 w-auto"
                />
              </a>
            </div>
            <div className="hidden md:flex space-x-8">
              <a
                href="#about"
                className="text-gray-700 hover:text-blue-600 font-medium cursor-pointer"
              >
                会社概要
              </a>
              <a
                href="#service"
                className="text-gray-700 hover:text-blue-600 font-medium cursor-pointer"
              >
                サービス紹介
              </a>
              <a
                href="#news"
                className="text-gray-700 hover=text-blue-600 font-medium cursor-pointer"
              >
                ニュース
              </a>
              <a
                href="#ir"
                className="text-gray-700 hover:text-blue-600 font-medium cursor-pointer"
              >
                IR情報
              </a>
              <a
                href="#careers"
                className="text-gray-700 hover:text-blue-600 font-medium cursor-pointer"
              >
                採用情報
              </a>
            </div>
            <div className="hidden md:block">
              <a
                href="#contact"
                className="bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                お問い合わせ
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* ヒーローエリア */}
      <section
        className="relative min-h-screen flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(10, 37, 64, 0.8), rgba(10, 37, 64, 0.8)), url('https://readdy.ai/api/search-image?query=Professional%20soft%20tennis%20match%20in%20action%20with%20two%20players%20competing%20on%20outdoor%20court%2C%20dynamic%20sports%20photography%20showing%20players%20mid-rally%20with%20rackets%20and%20ball%20visible%2C%20spectators%20watching%20from%20sidelines%2C%20clear%20blue%20sky%20background%2C%20energetic%20competitive%20atmosphere%2C%20high%20quality%20sports%20action%20shot%20capturing%20the%20intensity%20of%20soft%20tennis%20gameplay&width=1920&height=1080&seq=hero-softtennis-match&orientation=landscape')`,
        }}
      >
        <div className="container mx-auto px-4 sm:px-6 text-center text-white">
          <div className="max-w-5xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              スポーツの力で、<br className="hidden sm:block" />
              人と社会に笑顔とつながりを
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl mb-8 opacity-90 px-4 font-medium">
              株式会社クロポは、ソフトテニスをはじめとした
              <br className="hidden sm:block" />
              大会運営をDXで支援します
            </p>
          </div>
        </div>
      </section>

      {/* 会社概要 */}
      <section id="about" className="py-16 lg:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2
              className="text-3xl lg:text-4xl font-bold mb-4"
              style={{ color: '#0A2540' }}
            >
              会社概要
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              株式会社クロポは、スポーツDXを通じて新しい価値を創造します
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-24 text-sm font-semibold text-gray-500 mt-1">
                    社名
                  </div>
                  <div className="flex-1 font-medium text-gray-900">
                    株式会社クロポ（XROPO Inc.）
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-24 text-sm font-semibold text-gray-500 mt-1">
                    設立
                  </div>
                  <div className="flex-1 font-medium text-gray-900">
                    2025年9月
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-24 text-sm font-semibold text-gray-500 mt-1">
                    資本金
                  </div>
                  <div className="flex-1 font-medium text-gray-900">
                    200万円
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-24 text-sm font-semibold text-gray-500 mt-1">
                    代表取締役
                  </div>
                  <div className="flex-1 font-medium text-gray-900">
                    赤羽根 啓輔
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-24 text-sm font-semibold text-gray-500 mt-1">
                    所在地
                  </div>
                  <div className="flex-1 font-medium text-gray-900">
                    〒101-0041<br />
                    東京都千代田区神田須田町1丁目7番8号　VORT秋葉原Ⅳ 2F
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold mb-4" style={{ color: '#0A2540' }}>
                    アクセス
                  </h3>
                  <div className="w-full h-80 rounded-lg overflow-hidden shadow-md">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3240.827!2d139.7731!3d35.6984!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x60188ea73ea6f4ff%3A0x1234567890abcdef!2z44CSMTAxLTAwNDEg5p2x5Lqs6YO95Y2D5Luj55Sw5Yy656We55Sw6aG76JCh55S677yR5LiB55uu77yX4oiS77yY44CA!5e0!3m2!1sja!2sjp!4v1234567890123!5m2!1sja!2sjp"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="株式会社クロポ所在地"
                    ></iframe>
                  </div>
                  <div className="mt-4 text-sm text-gray-600">
                    <p className="mb-2">
                      <i className="ri-train-fill mr-2" style={{ color: '#0A2540' }}></i>
                      JR山手線・京浜東北線「秋葉原駅」徒歩5分
                    </p>
                    <p className="mb-2">
                      <i className="ri-train-fill mr-2" style={{ color: '#0A2540' }}></i>
                      東京メトロ日比谷線「秋葉原駅」徒歩3分
                    </p>
                    <p className="mb-2">
                      <i className="ri-train-fill mr-2" style={{ color: '#0A2540' }}></i>
                      つくばエクスプレス「秋葉原駅」徒歩4分
                    </p>
                    <p>
                      <i className="ri-train-fill mr-2" style={{ color: '#0A2540' }}></i>
                      都営新宿線「岩本町駅」徒歩5分
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MVV */}
      <section id="mvv" className="py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2
              className="text-3xl lg:text-4xl font-bold mb-4"
              style={{ color: '#0A2540' }}
            >
              Mission / Vision / Value
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              私たちの存在意義、目指す未来、そして行動指針
            </p>
          </div>

          <div className="space-y-16">
            {/* Mission */}
            <div className="bg-gray-50 p-8 lg:p-12 rounded-2xl">
              <div className="flex items-center mb-6">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center mr-4"
                  style={{ backgroundColor: '#00A6D6' }}
                >
                  <i className="ri-star-fill text-white text-xl"></i>
                </div>
                <h3
                  className="text-2xl lg:text-3xl font-bold"
                  style={{ color: '#0A2540' }}
                >
                  Mission（存在意義）
                </h3>
              </div>
              <div className="text-lg lg:text-xl text-gray-700 leading-relaxed">
                <p className="mb-4 font-semibold">
                  スポーツの力で、人と社会に"笑顔とつながり"を。
                </p>
                <p className="mb-4">
                  テクノロジーを活用し、大会運営をもっと楽しく・スムーズに。
                </p>
                <p>
                  選手・指導者・観客・運営者──すべての人が幸せになれるスポーツの仕組みをつくります。
                </p>
              </div>
            </div>

            {/* Vision */}
            <div className="bg-gray-50 p-8 lg:p-12 rounded-2xl">
              <div className="flex items-center mb-6">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center mr-4"
                  style={{ backgroundColor: '#FFB800' }}
                >
                  <i className="ri-rocket-fill text-white text-xl"></i>
                </div>
                <h3
                  className="text-2xl lg:text-3xl font-bold"
                  style={{ color: '#0A2540' }}
                >
                  Vision（目指す未来）
                </h3>
              </div>
              <div className="text-lg lg:text-xl text-gray-700 leading-relaxed">
                <p className="mb-6 font-semibold">
                  ソフトテニスから始めて、大会運営の新しい"当たり前"をつくります
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-3 mt-1">•</span>
                    全国の学生大会から国際大会まで使われるプラットフォームへ
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-3 mt-1">•</span>
                    誰もが簡単に大会を運営できる社会を実現
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-3 mt-1">•</span>
                    日本発のスポーツDXを、世界へ展開する
                  </li>
                </ul>
              </div>
            </div>

            {/* Value */}
            <div className="bg-gray-50 p-8 lg:p-12 rounded-2xl">
              <div className="flex items-center mb-6">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center mr-4"
                  style={{ backgroundColor: '#0A2540' }}
                >
                  <i className="ri-heart-3-fill text-white text-xl"></i>
                </div>
                <h3
                  className="text-2xl lg:text-3xl font-bold"
                  style={{ color: '#0A2540' }}
                >
                  Value（私たちの行動指針）
                </h3>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div>
                    <h4
                      className="text-lg font-bold mb-2"
                      style={{ color: '#0A2540' }}
                    >
                      1. 現場ファースト
                    </h4>
                    <p className="text-gray-700">
                      選手・指導者・運営者の声に耳を傾ける
                    </p>
                  </div>
                  <div>
                    <h4
                      className="text-lg font-bold mb-2"
                      style={{ color: '#0A2540' }}
                    >
                      2. 仲間の幸せがサービスの質になる
                    </h4>
                    <p className="text-gray-700">
                      社員の成長と安心を最優先に考える
                    </p>
                  </div>
                </div>
                <div className="space-y-6">
                  <div>
                    <h4
                      className="text-lg font-bold mb-2"
                      style={{ color: '#0A2540' }}
                    >
                      3. テクノロジーで「楽しい」を広げる
                    </h4>
                    <p className="text-gray-700">
                      効率化だけでなく、ワクワクを増やす仕組みを生む
                    </p>
                  </div>
                  <div>
                    <h4
                      className="text-lg font-bold mb-2"
                      style={{ color: '#0A2540' }}
                    >
                      4. 挑戦と進化
                    </h4>
                    <p className="text-gray-700">
                      改善と挑戦をやめず、スポーツ界に新しい風を届ける
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* サービス紹介 */}
      <section id="service" className="py-16 lg:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2
              className="text-3xl lg:text-4xl font-bold mb-4"
              style={{ color: '#0A2540' }}
            >
              サービス紹介
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              ソフトテニス大会運営アプリ「XROPO」で、大会運営を革新します
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h3
                className="text-2xl lg:text-3xl font-bold mb-6"
                style={{ color: '#0A2540' }}
              >
                ソフトテニス大会運営アプリ XROPO
              </h3>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                従来の紙ベースの大会運営から脱却し、デジタル技術を活用した効率的で透明性の高い大会運営を実現します。
              </p>

              <div className="space-y-6">
                <div className="flex items-start">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center mr-4 mt-1"
                    style={{ backgroundColor: '#00A6D6' }}
                  >
                    <i className="ri-settings-3-fill text-white text-sm"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2" style={{ color: '#0A2540' }}>
                      スマート運営
                    </h4>
                    <p className="text-gray-600">
                      試合進行・対戦表を自動生成。煩雑な手作業から解放され、運営効率が大幅に向上します。
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center mr-4 mt-1"
                    style={{ backgroundColor: '#00A6D6' }}
                  >
                    <i className="ri-share-fill text-white text-sm"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2" style={{ color: '#0A2540' }}>
                      リアルタイム共有
                    </h4>
                    <p className="text-gray-600">
                      観客・選手もスマホでリアルタイムに試合状況をチェック。みんなで大会を楽しめます。
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center mr-4 mt-1"
                    style={{ backgroundColor: '#00A6D6' }}
                  >
                    <i className="ri-arrow-up-fill text-white text-sm"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2" style={{ color: '#0A2540' }}>
                      スケールアップ可能
                    </h4>
                    <p className="text-gray-600">
                      学生大会から全国大会まで対応。規模に応じて柔軟にシステムを拡張できます。
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8">
              <a
                href="#contact"
                className="inline-block text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer bg-[#FFB800] hover:bg-[#E6A600]"
              >
                導入相談はこちら
              </a>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20mobile%20app%20interface%20showing%20tournament%20bracket%20management%20system%2C%20clean%20UI%20design%20with%20blue%20and%20white%20colors%2C%20professional%20sports%20application%20screenshot%2C%20user-friendly%20dashboard%20with%20match%20results%20and%20player%20information&width=600&height=400&seq=service-app&orientation=landscape"
                alt="XROPO アプリ画面"
                className="w-full rounded-xl shadow-lg object-cover"
              />
            </div>
          </div>

          {/* 導入メリット */}
          <div className="bg-white p-8 lg:p-12 rounded-2xl shadow-lg">
            <h3
              className="text-2xl lg:text-3xl font-bold mb-8 text-center"
              style={{ color: '#0A2540' }}
            >
              導入メリット
            </h3>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: '#FFB800' }}
                >
                  <i className="ri-money-dollar-circle-fill text-white text-2xl"></i>
                </div>
                <h4 className="text-lg font-bold mb-3" style={{ color: '#0A2540' }}>
                  運営コスト削減
                </h4>
                <p className="text-gray-600">
                  紙の印刷費用や人件費を大幅に削減し、効率的な大会運営を実現
                </p>
              </div>

              <div className="text-center">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: '#00A6D6' }}
                >
                  <i className="ri-shield-check-fill text-white text-2xl"></i>
                </div>
                <h4 className="text-lg font-bold mb-3" style={{ color: '#0A2540' }}>
                  公平性の確保
                </h4>
                <p className="text-gray-600">
                  デジタル抽選システムにより、透明性の高い組み合わせ決定を実現
                </p>
              </div>

              <div className="text-center">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: '#0A2540' }}
                >
                  <i className="ri-heart-fill text-white text-2xl"></i>
                </div>
                <h4 className="text-lg font-bold mb-3" style={{ color: '#0A2540' }}>
                  参加者満足度向上
                </h4>
                <p className="text-gray-600">
                  リアルタイム情報配信により、選手・保護者の満足度を大幅に向上
                </p>
              </div>
            </div>
          </div>

          {/* 導入実績 */}
          <div className="mt-16">
            <div className="text-center mb-12">
              <h3
                className="text-2xl lg:text-3xl font-bold mb-4"
                style={{ color: '#0A2540' }}
              >
                導入実績
              </h3>
              <p className="text-lg text-gray-600">
                全国の大会でXROPOが活用されています
              </p>
            </div>

            <div className="bg-white p-8 lg:p-12 rounded-2xl shadow-lg">
              <div className="flex items-center mb-8">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center mr-4"
                  style={{ backgroundColor: '#00A6D6' }}
                >
                  <i className="ri-flask-fill text-white text-xl"></i>
                </div>
                <h4
                  className="text-xl lg:text-2xl font-bold"
                  style={{ color: '#0A2540' }}
                >
                  実証実験実施
                </h4>
              </div>

              <p className="text-lg text-gray-700 mb-8">
                以下の大会でXROPOの実証実験を行いました
              </p>

              <div className="bg-gray-50 p-6 lg:p-8 rounded-xl">
                <div className="grid lg:grid-cols-2 gap-8 items-center">
                  <div>
                    <h5
                      className="text-xl font-bold mb-4"
                      style={{ color: '#0A2540' }}
                    >
                      ポカリスエット杯BIGCONC<br />
                      ソフトテニスチャレンジカップシングルス2025
                    </h5>

                    <div className="space-y-3">
                      <div className="flex items-center">
                        <div
                          className="w-6 h-6 rounded-full flex items-center justify-center mr-3"
                          style={{ backgroundColor: '#FFB800' }}
                        >
                          <i className="ri-calendar-fill text-white text-xs"></i>
                        </div>
                        <span className="font-semibold text-gray-700">
                          2025年2月15日（SAT）
                        </span>
                      </div>

                      <div className="flex items-center">
                        <div
                          className="w-6 h-12 rounded-full flex items-center justify-center mr-3"
                          style={{ backgroundColor: '#00A6D6' }}
                        >
                          <i className="ri-map-pin-fill text-white text-xs"></i>
                        </div>
                        <span className="font-semibold text-gray-700">
                          J:COM北市川スポーツパーク
                        </span>
                      </div>
                    </div>

                    <div
                      className="mt-6 p-4 bg-white rounded-lg border-l-4"
                      style={{ borderColor: '#00A6D6' }}
                    >
                      <p className="text-sm text-gray-600">
                        <strong>実証実験の成果：</strong><br />
                        デジタル化により大会運営の効率が大幅に向上し、参加者からも高い評価をいただきました。
                      </p>
                    </div>
                  </div>

                  <div>
                    <img
                      src="https://readdy.ai/api/search-image?query=Professional%20soft%20tennis%20tournament%20at%20modern%20sports%20facility%2C%20players%20competing%20on%20outdoor%20courts%20with%20spectators%2C%20tournament%20banners%20and%20digital%20scoreboards%2C%20organized%20sports%20event%20atmosphere%20with%20clear%20blue%20sky&width=500&height=350&seq=tournament-demo&orientation=landscape"
                      alt="実証実験の様子"
                      className="w-full rounded-lg shadow-md object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      
      {/* SES事業 */}
<div className="mt-16 bg-white p-8 lg:p-12 rounded-2xl shadow-lg">
  <div className="text-center mb-12">
    <h3
      className="text-2xl lg:text-3xl font-bold mb-4"
      style={{ color: '#0A2540' }}
    >
      SES事業
    </h3>
    <p className="text-lg text-gray-600 max-w-3xl mx-auto">
      開発現場の課題に寄り添い、プロジェクト推進を支援します。
    </p>
  </div>

  <div className="grid lg:grid-cols-2 gap-12 items-center">
    {/* 左：説明 */}
    <div>
      <h4
        className="text-xl lg:text-2xl font-bold mb-6"
        style={{ color: '#0A2540' }}
      >
        現場に寄り添うエンジニアリング支援
      </h4>

      <p className="text-lg text-gray-700 mb-8 leading-relaxed">
        要件定義から設計・実装・テスト・運用まで、
        チームの状況に合わせて柔軟に参画いたします。
      </p>

      <div className="space-y-6">
        <div className="flex items-start">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center mr-4 mt-1"
            style={{ backgroundColor: '#00A6D6' }}
          >
            <i className="ri-team-fill text-white text-sm"></i>
          </div>
          <div>
            <h5 className="font-semibold mb-2" style={{ color: '#0A2540' }}>
              チーム開発への参画
            </h5>
            <p className="text-gray-600">
              既存メンバーと連携しながら、スムーズにプロジェクトを推進します。
            </p>
          </div>
        </div>

        <div className="flex items-start">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center mr-4 mt-1"
            style={{ backgroundColor: '#00A6D6' }}
          >
            <i className="ri-code-s-slash-line text-white text-sm"></i>
          </div>
          <div>
            <h5 className="font-semibold mb-2" style={{ color: '#0A2540' }}>
              Web・業務システム開発
            </h5>
            <p className="text-gray-600">
              フロントエンドからバックエンドまで幅広く対応可能です。
            </p>
          </div>
        </div>

        <div className="flex items-start">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center mr-4 mt-1"
            style={{ backgroundColor: '#00A6D6' }}
          >
            <i className="ri-shield-check-fill text-white text-sm"></i>
          </div>
          <div>
            <h5 className="font-semibold mb-2" style={{ color: '#0A2540' }}>
              品質を重視した開発
            </h5>
            <p className="text-gray-600">
              レビューやテストを通じて、保守性の高い実装を行います。
            </p>
          </div>
        </div>
      </div>

      <div className="mt-10">
      <a
        href="#contact"
        className="inline-block text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer bg-[#FFB800] hover:bg-[#E6A600]"
      >
        SESのご相談はこちら
      </a>
        {/* <button
          className="text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
          style={{ backgroundColor: '#FFB800' }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = '#E6A600')
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = '#FFB800')
          }
        >
          SESのご相談はこちら
        </button> */}
      </div>
    </div>

    {/* 右：ビジュアル */}
    <div>
      <img
        src={sesImage}
        alt="SES事業の開発支援イメージ"
        className="w-full h-[400px] rounded-xl shadow-md object-cover"
      />
    </div>
  </div>
</div>
      </section>

      {/* クロポを支える技術力 */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2
              className="text-3xl lg:text-4xl font-bold mb-4"
              style={{ color: '#0A2540' }}
            >
              クロポを支える技術力
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              最新のテクノロジーと豊富な経験で、信頼性の高いサービスを提供します
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20software%20development%20team%20working%20on%20sports%20technology%20application%2C%20developers%20coding%20on%20multiple%20monitors%20showing%20tournament%20management%20interface%2C%20clean%20office%20environment%20with%20blue%20and%20white%20tech%20aesthetic%2C%20professional%20programming%20workspace&width=600&height=400&seq=tech-team&orientation=landscape"
                alt="技術開発チーム"
                className="w-full rounded-xl shadow-lg object-cover"
              />
            </div>

            <div className="space-y-8">
              <div>
                <h3
                  className="text-2xl lg:text-3xl font-bold mb-6"
                  style={{ color: '#0A2540' }}
                >
                  確かな技術基盤
                </h3>
                <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                  代表の赤羽根は、大手IT企業での豊富な開発経験を活かし、スケーラブルで安全なシステム設計を実現しています。
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center mr-4 mt-1"
                    style={{ backgroundColor: '#00A6D6' }}
                  >
                    <i className="ri-cloud-fill text-white text-sm"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2" style={{ color: '#0A2540' }}>
                      クラウドネイティブ設計
                    </h4>
                    <p className="text-gray-600">
                      AWS・Azureを活用した高可用性・高拡張性のインフラ構築で、大規模大会にも対応
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center mr-4 mt-1"
                    style={{ backgroundColor: '#00A6D6' }}
                  >
                    <i className="ri-smartphone-fill text-white text-sm"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2" style={{ color: '#0A2540' }}>
                      モバイルファースト開発
                    </h4>
                    <p className="text-gray-600">
                      React Native・Flutterによるクロスプラットフォーム対応で、どのデバイスでも快適に利用可能
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center mr-4 mt-1"
                    style={{ backgroundColor: '#00A6D6' }}
                  >
                    <i className="ri-shield-check-fill text-white text-sm"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2" style={{ color: '#0A2540' }}>
                      セキュリティ重視
                    </h4>
                    <p className="text-gray-600">
                      個人情報保護・データ暗号化・アクセス制御により、安心してご利用いただけるシステムを構築
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 技術スタック */}
          <div className="bg-gray-50 p-8 lg:p-12 rounded-2xl">
            <h3
              className="text-2xl lg:text-3xl font-bold mb-8 text-center"
              style={{ color: '#0A2540' }}
            >
              採用技術スタック
            </h3>
            <div className="grid md:grid-cols-4 gap-8">
              <div className="text-center">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: '#FFB800' }}
                >
                  <i className="ri-code-s-slash-fill text-white text-2xl"></i>
                </div>
                <h4 className="text-lg font-bold mb-3" style={{ color: '#0A2540' }}>
                  フロントエンド
                </h4>
                <p className="text-gray-600 text-sm">
                  React, TypeScript<br />
                  Next.js, Tailwind CSS
                </p>
              </div>

              <div className="text-center">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: '#00A6D6' }}
                >
                  <i className="ri-server-fill text-white text-2xl"></i>
                </div>
                <h4 className="text-lg font-bold mb-3" style={{ color: '#0A2540' }}>
                  バックエンド
                </h4>
                <p className="text-gray-600 text-sm">
                  Node.js, Python<br />
                  Express, FastAPI
                </p>
              </div>

              <div className="text-center">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: '#0A2540' }}
                >
                  <i className="ri-database-2-fill text-white text-2xl"></i>
                </div>
                <h4 className="text-lg font-bold mb-3" style={{ color: '#0A2540' }}>
                  データベース
                </h4>
                <p className="text-gray-600 text-sm">
                  PostgreSQL, Redis<br />
                  MongoDB, Elasticsearch
                </p>
              </div>

              <div className="text-center">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: '#FFB800' }}
                >
                  <i className="ri-cloud-fill text-white text-2xl"></i>
                </div>
                <h4 className="text-lg font-bold mb-3" style={{ color: '#0A2540' }}>
                  インフラ
                </h4>
                <p className="text-gray-600 text-sm">
                  AWS, Azure<br />
                  Docker, Kubernetes
                </p>
              </div>
            </div>
          </div>

          {/* 開発体制 */}
          <div className="mt-16 bg-white p-8 lg:p-12 rounded-2xl shadow-lg">
            <h3
              className="text-2xl lg:text-3xl font-bold mb-8 text-center"
              style={{ color: '#0A2540' }}
            >
              開発体制・品質管理
            </h3>
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <h4
                  className="text-xl font-bold mb-6"
                  style={{ color: '#0A2540' }}
                >
                  アジャイル開発
                </h4>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center mr-3 mt-1"
                      style={{ backgroundColor: '#00A6D6' }}
                    >
                      <i className="ri-refresh-line text-white text-xs"></i>
                    </div>
                    <p className="text-gray-600">
                      2週間スプリットでの継続的な機能改善
                    </p>
                  </div>

                  <div className="flex items-start">
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center mr-3 mt-1"
                      style={{ backgroundColor: '#00A6D6' }}
                    >
                      <i className="ri-team-fill text-white text-xs"></i>
                    </div>
                    <p className="text-gray-600">
                      ユーザーフィードバックを重視した開発プロセス
                    </p>
                  </div>

                <div className="flex items-start">
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center mr-3 mt-1"
                    style={{ backgroundColor: '#00A6D6' }}
                  >
                    <i className="ri-git-branch-fill text-white text-xs"></i>
                  </div>
                  <p className="text-gray-600">
                    Git Flow による安全なコード管理
                  </p>
                </div>
                </div>
              </div>

              <div>
                <h4
                  className="text-xl font-bold mb-6"
                  style={{ color: '#0A2540' }}
                >
                  品質保証
                </h4>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center mr-3 mt-1"
                      style={{ backgroundColor: '#FFB800' }}
                    >
                      <i className="ri-bug-fill text-white text-xs"></i>
                    </div>
                    <p className="text-gray-600">
                      自動テスト・手動テストによる多層的な品質チェック
                    </p>
                  </div>

                  <div className="flex items-start">
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center mr-3 mt-1"
                      style={{ backgroundColor: '#FFB800' }}
                    >
                      <i className="ri-eye-fill text-white text-xs"></i>
                    </div>
                    <p className="text-gray-600">
                      コードレビュー・ペアプログラミングによる品質向上
                    </p>
                  </div>

                  <div className="flex items-start">
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center mr-3 mt-1"
                      style={{ backgroundColor: '#FFB800' }}
                    >
                      <i className="ri-shield-check-fill text-white text-xs"></i>
                    </div>
                    <p className="text-gray-600">
                      セキュリティ監査・脆弱性診断の定期実施
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 採用情報 */}
      <section id="careers" className="py-16 lg:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2
              className="text-3xl lg:text-4xl font-bold mb-4"
              style={{ color: '#0A2540' }}
            >
              採用情報
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              スポーツの力で社会を変える仲間を募集しています
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h3
                className="text-2xl lg:text-3xl font-bold mb-6"
                style={{ color: '#0A2540' }}
              >
                一緒にスポーツの未来を創りませんか？
              </h3>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                株式会社クロポでは、スポーツDXを通じて新しい価値を創造する仲間を募集しています。技術力とスポーツへの情熱を持った方をお待ちしています。
              </p>

              <div className="space-y-6">
                <div className="flex items-start">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center mr-4 mt-1"
                    style={{ backgroundColor: '#00A6D6' }}
                  >
                    <i className="ri-rocket-fill text-white text-sm"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2" style={{ color: '#0A2540' }}>
                      成長できる環境
                    </h4>
                    <p className="text-gray-600">
                      最新技術を学び、スポーツ業界に革新をもたらすプロダクト開発に携われます
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center mr-4 mt-1"
                    style={{ backgroundColor: '#00A6D6' }}
                  >
                    <i className="ri-team-fill text-white text-sm"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2" style={{ color: '#0A2540' }}>
                      フラットな組織
                    </h4>
                    <p className="text-gray-600">
                      年齢や経験に関係なく、アイデアを提案し実現できる環境があります
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center mr-4 mt-1"
                    style={{ backgroundColor: '#00A6D6' }}
                  >
                    <i className="ri-heart-fill text-white text-sm"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2" style={{ color: '#0A2540' }}>
                      社会貢献
                    </h4>
                    <p className="text-gray-600">
                      スポーツを通じて人々に笑顔とつながりを届ける、意義のある仕事です
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Diverse%20team%20of%20young%20professionals%20working%20together%20in%20modern%20office%20environment%2C%20collaborative%20workspace%20with%20laptops%20and%20whiteboards%2C%20energetic%20startup%20atmosphere%20with%20natural%20lighting%20%2C%20people%20discussing%20sports%20technology%20projects&width=600&height=400&seq=careers-team&orientation=landscape"
                alt="チームワーク"
                className="w-full rounded-xl shadow-lg object-cover"
              />
            </div>
          </div>

          {/* 募集職種 */}
          <div className="bg-white p-8 lg:p-12 rounded-2xl shadow-lg mb-16">
            <h3
              className="text-2xl lg:text-3xl font-bold mb-8 text-center"
              style={{ color: '#0A2540' }}
            >
              募集職種
            </h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="border border-gray-200 p-6 rounded-xl">
                <div className="flex items-center mb-4">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center mr-4"
                    style={{ backgroundColor: '#FFB800' }}
                  >
                    <i className="ri-code-s-slash-fill text-white text-xl"></i>
                  </div>
                  <h4 className="text-xl font-bold" style={{ color: '#0A2540' }}>
                    フロントエンドエンジニア
                  </h4>
                </div>
                <p className="text-gray-600 mb-4">
                  React/TypeScriptを使用したWebアプリケーション開発
                </p>
                <div className="space-y-2">
                  <div className="flex items-center text-sm text-gray-600">
                    <i className="ri-check-fill text-green-500 mr-2"></i>
                    React, TypeScript経験2年以上
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <i className="ri-check-fill text-green-500 mr-2"></i>
                    UI/UXに対する関心
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <i className="ri-check-fill text-green-500 mr-2"></i>
                    スポーツへの興味・関心
                  </div>
                </div>
              </div>

              <div className="border border-gray-200 p-6 rounded-xl">
                <div className="flex items-center mb-4">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center mr-4"
                    style={{ backgroundColor: '#00A6D6' }}
                  >
                    <i className="ri-server-fill text-white text-xl"></i>
                  </div>
                  <h4 className="text-xl font-bold" style={{ color: '#0A2540' }}>
                    バックエンドエンジニア
                  </h4>
                </div>
                <p className="text-gray-600 mb-4">
                  Node.js/Pythonを使用したAPI開発・インフラ構築
                </p>
                <div className="space-y-2">
                  <div className="flex items-center text-sm text-gray-600">
                    <i className="ri-check-fill text-green-500 mr-2"></i>
                    Node.js または Python経験2年以上
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <i className="ri-check-fill text-green-500 mr-2"></i>
                    AWS/Azure等クラウド経験
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <i className="ri-check-fill text-green-500 mr-2"></i>
                    データベース設計経験
                  </div>
                </div>
              </div>

              <div className="border border-gray-200 p-6 rounded-xl">
                <div className="flex items-center mb-4">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center mr-4"
                    style={{ backgroundColor: '#0A2540' }}
                  >
                    <i className="ri-smartphone-fill text-white text-xl"></i>
                  </div>
                  <h4 className="text-xl font-bold" style={{ color: '#0A2540' }}>
                    モバイルアプリエンジニア
                  </h4>
                </div>
                <p className="text-gray-600 mb-4">
                  React Native/Flutterを使用したモバイルアプリ開発
                </p>
                <div className="space-y-2">
                  <div className="flex items-center text-sm text-gray-600">
                    <i className="ri-check-fill text-green-500 mr-2"></i>
                    React Native または Flutter経験
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <i className="ri-check-fill text-green-500 mr-2"></i>
                    iOS/Android開発経験
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <i className="ri-check-fill text-green-500 mr-2"></i>
                    ユーザビリティへの理解
                  </div>
                </div>
              </div>

              <div className="border border-gray-200 p-6 rounded-xl">
                <div className="flex items-center mb-4">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center mr-4"
                    style={{ backgroundColor: '#FFB800' }}
                  >
                    <i className="ri-lightbulb-fill text-white text-xl"></i>
                  </div>
                  <h4 className="text-xl font-bold" style={{ color: '#0A2540' }}>
                    プロダクトマネージャー
                  </h4>
                </div>
                <p className="text-gray-600 mb-4">
                  プロダクト戦略立案・要件定義・プロジェクト管理
                </p>
                <div className="space-y-2">
                  <div className="flex items-center text-sm text-gray-600">
                    <i className="ri-check-fill text-green-500 mr-2"></i>
                    プロダクト開発経験3年以上
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <i className="ri-check-fill text-green-500 mr-2"></i>
                    アジャイル開発経験
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <i className="ri-check-fill text-green-500 mr-2"></i>
                    スポーツ業界への深い理解
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 働く環境・福利厚生 */}
          <div className="bg-white p-8 lg:p-12 rounded-2xl shadow-lg">
            <h3
              className="text-2xl lg:text-3xl font-bold mb-8 text-center"
              style={{ color: '#0A2540' }}
            >
              働く環境・福利厚生
            </h3>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: '#00A6D6' }}
                >
                  <i className="ri-time-fill text-white text-2xl"></i>
                </div>
                <h4 className="text-lg font-bold mb-3" style={{ color: '#0A2540' }}>
                  フレックスタイム制
                </h4>
                <p className="text-gray-600">
                  コアタイム11:00-15:00で、ライフスタイルに合わせた働き方が可能
                </p>
              </div>

              <div className="text-center">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: '#FFB800' }}
                >
                  <i className="ri-home-wifi-fill text-white text-2xl"></i>
                </div>
                <h4 className="text-lg font-bold mb-3" style={{ color: '#0A2540' }}>
                  リモートワーク可
                </h4>
                <p className="text-gray-600">
                  週2-3日のリモートワークで、効率的な働き方をサポート
                </p>
              </div>

              <div className="text-center">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: '#0A2540' }}
                >
                  <i className="ri-book-open-fill text-white text-2xl"></i>
                </div>
                <h4 className="text-lg font-bold mb-3" style={{ color: '#0A2540' }}>
                  学習支援制度
                </h4>
                <p className="text-gray-600">
                  技術書籍購入費・セミナー参加費を会社が全額負担
                </p>
              </div>

              <div className="text-center">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: '#00A6D6' }}
                >
                  <i className="ri-heart-pulse-fill text-white text-2xl"></i>
                </div>
                <h4 className="text-lg font-bold mb-3" style={{ color: '#0A2540' }}>
                  健康管理サポート
                </h4>
                <p className="text-gray-600">
                  健康診断・人間ドック費用補助、メンタルヘルスケア
                </p>
              </div>

              <div className="text-center">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: '#0A2540' }}
                >
                  <i className="ri-gift-fill text-white text-2xl"></i>
                </div>
                <h4 className="text-lg font-bold mb-3" style={{ color: '#0A2540' }}>
                  各種手当
                </h4>
                <p className="text-gray-600">
                  交通費全額支給など、働きやすさを支える各種手当を整備
                </p>
              </div>
            </div>

            <div className="mt-12 text-center">
              <a
                href="#contact"
                className="text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
                style={{ backgroundColor: '#FFB800' }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = '#E6A600')
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = '#FFB800')
                }
              >
                採用に関するお問い合わせ
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ニュース */}
      <section id="news" className="py-16 lg:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2
              className="text-3xl lg:text-4xl font-bold mb-4"
              style={{ color: '#0A2540' }}
            >
              ニュース
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              最新の情報をお届けします
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <div className="mb-4">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20mobile%20app%20beta%20release%20announcement%20with%20smartphone%20displaying%20sports%20tournament%20management%20interface%2C%20professional%20tech%20product%20launch%20imagery%2C%20clean%20blue%20and%20white%20design%20elements%2C%20digital%20innovation%20in%20sports%20technology&width=400&height=200&seq=news-beta-release&orientation=landscape"
                  alt="XROPOβ版リリース"
                  className="w-full h-32 object-cover rounded-lg mb-4"
                />
                <span
                  className="inline-block px-3 py-1 text-xs font-semibold rounded-full text-white"
                  style={{ backgroundColor: '#00A6D6' }}
                >
                  プレスリリース
                </span>
              </div>
              <h3 className="text-lg font-bold mb-3" style={{ color: '#0A2540' }}>
                XROPOβ版リリースのお知らせ
              </h3>
              <p className="text-gray-600 text-sm mb-4">
                ソフトテニス大会運営アプリ「XROPO」のβ版をリリースいたしました。実証機能の検証を行います。
              </p>
              <div className="text-xs text-gray-500">
                2025年1月15日
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg">
              <div className="mb-4">
                <img
                  src="https://readdy.ai/api/search-image?query=Coming%20soon%20placeholder%20image%20with%20modern%20minimalist%20design%2C%20soft%20blue%20and%20gray%20gradient%20background%2C%20professional%20business%20announcement%20style%2C%20clean%20typography%20indicating%20preparation%20in%20progress&width=400&height=200&seq=news-coming-soon-1&orientation=landscape"
                  alt="準備中"
                  className="w-full h-32 object-cover rounded-lg mb-4"
                />
                <span
                  className="inline-block px-3 py-1 text-xs font-semibold rounded-full text-white"
                  style={{ backgroundColor: '#FFB800' }}
                >
                  準備中
                </span>
              </div>
              <h3 className="text-lg font-bold mb-3" style={{ color: '#0A2540' }}>
                準備中
              </h3>
              <p className="text-gray-600 text-sm mb-4">
                新しいニュースを準備中です。しばらくお待ちください。
              </p>
              <div className="text-xs text-gray-500">
                -
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg">
              <div className="mb-4">
                <img
                  src="https://readdy.ai/api/search-image?query=Coming%20soon%20placeholder%20image%20with%20modern%20minimalist%20design%2C%20soft%20blue%20and%20gray%20gradient%20background%2C%20professional%20business%20announcement%20style%2C%20clean%20typography%20indicating%20preparation%20in%20progress&width=400&height=200&seq=news-coming-soon-2&orientation=landscape"
                  alt="準備中"
                  className="w-full h-32 object-cover rounded-lg mb-4"
                />
                <span
                  className="inline-block px-3 py-1 text-xs font-semibold rounded-full text-white"
                  style={{ backgroundColor: '#FFB800' }}
                >
                  準備中
                </span>
              </div>
              <h3 className="text-lg font-bold mb-3" style={{ color: '#0A2540' }}>
                準備中
              </h3>
              <p className="text-gray-600 text-sm mb-4">
                新しいニュースで準備中です。しばらくお待ちください。
              </p>
              <div className="text-xs text-gray-500">
                -
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* IR情報 */}
      <section id="ir" className="py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2
              className="text-3xl lg:text-4xl font-bold mb-4"
              style={{ color: '#0A2540' }}
            >
              IR情報
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              株主・投資家の皆様への情報開示
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {/* 決算公告 */}
            <div className="bg-gray-50 p-8 lg:p-12 rounded-2xl shadow-lg mb-12">
              <div className="flex items-center mb-8">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center mr-4"
                  style={{ backgroundColor: '#00A6D6' }}
                >
                  <i className="ri-file-text-fill text-white text-xl"></i>
                </div>
                <h3
                  className="text-2xl lg:text-3xl font-bold"
                  style={{ color: '#0A2540' }}
                >
                  決算公告
                </h3>
              </div>

              <p className="text-lg text-gray-700 mb-8">
                会社法第440条に基づく電子公告として、決算書類を公開しています。
              </p>

              <div className="space-y-6">
                {/* 第1期決算公告 */}
                <div className="bg-white p-6 lg:p-8 rounded-xl border border-gray-200 opacity-60">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                    <div className="mb-4 lg:mb-0">
                      <h4
                        className="text-xl font-bold mb-3 text-gray-500"
                      >
                        第1期決算公告（2025年度）
                      </h4>
                      <div className="space-y-2">
                        <div className="flex items-center text-gray-500">
                          <i className="ri-calendar-line mr-2"></i>
                          <span className="font-semibold">決算期：</span>
                          <span className="ml-2">2025年9月1日〜2026年8月31日</span>
                        </div>
                        <div className="flex items-center text-gray-500">
                          <i className="ri-time-line mr-2"></i>
                          <span className="font-semibold">公告予定日：</span>
                          <span className="ml-2">2026年11月30日</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex-shrink-0">
                      <button
                        disabled
                        className="bg-gray-300 text-gray-500 px-6 py-3 rounded-lg font-semibold cursor-not-allowed flex items-center"
                      >
                        <i className="ri-time-line mr-2"></i>
                        準備中
                      </button>
                    </div>
                  </div>
                </div>

                {/* 第1期決算公告(後日公開予定) */}
                {/* <div className="bg-white p-6 lg:p-8 rounded-xl border border-gray-200">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                    <div className="mb-4 lg:mb-0">
                      <h4
                        className="text-xl font-bold mb-3"
                        style={{ color: '#0A2540' }}
                      >
                        第1期決算公告（2025年度）
                      </h4>
                      <div className="space-y-2">
                        <div className="flex items-center text-gray-600">
                          <i className="ri-calendar-line mr-2" style={{ color: '#00A6D6' }}></i>
                          <span className="font-semibold">決算期：</span>
                          <span className="ml-2">2025年9月1日〜2026年8月31日</span>
                        </div>
                        <div className="flex items-center text-gray-600">
                          <i className="ri-building-line mr-2" style={{ color: '#00A6D6' }}></i>
                          <span className="font-semibold">公告日：</span>
                          <span className="ml-2">2026年11月30日</span>
                        </div>
                      </div>

                      <div className="mt-4 p-4 bg-blue-50 rounded-lg border-l-4" style={{ borderColor: '#00A6D6' }}>
                        <p className="text-sm text-gray-700">
                          <strong>第1期業績概要：</strong><br />
                          設立初年度として、XROPOアプリの開発・実証実験を中心とした事業基盤の構築を行いました。
                        </p>
                      </div>
                    </div>

                    <div className="flex-shrink-0">
                      <a
                        href="/financial-disclosure"
                        className="text-white px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer flex items-center"
                        style={{ backgroundColor: '#00A6D6' }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#FFB800')}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#00A6D6')}
                      >
                        <i className="ri-file-pdf-line mr-2"></i>
                        決算公告を見る
                      </a>
                    </div>
                  </div>
                </div> */}

                {/* 第2期以降（予定） */}
                <div className="bg-white p-6 lg:p-8 rounded-xl border border-gray-200 opacity-60">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                    <div className="mb-4 lg:mb-0">
                      <h4
                        className="text-xl font-bold mb-3 text-gray-500"
                      >
                        第2期決算公告（2026年度）
                      </h4>
                      <div className="space-y-2">
                        <div className="flex items-center text-gray-500">
                          <i className="ri-calendar-line mr-2"></i>
                          <span className="font-semibold">決算期：</span>
                          <span className="ml-2">2026年9月1日〜2027年8月31日</span>
                        </div>
                        <div className="flex items-center text-gray-500">
                          <i className="ri-time-line mr-2"></i>
                          <span className="font-semibold">公告予定日：</span>
                          <span className="ml-2">2027年11月30日</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex-shrink-0">
                      <button
                        disabled
                        className="bg-gray-300 text-gray-500 px-6 py-3 rounded-lg font-semibold cursor-not-allowed flex items-center"
                      >
                        <i className="ri-time-line mr-2"></i>
                        準備中
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 会社情報 */}
            <div className="bg-gray-50 p-8 lg:p-12 rounded-2xl shadow-lg">
              <div className="flex items-center mb-8">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center mr-4"
                  style={{ backgroundColor: '#FFB800' }}
                >
                  <i className="ri-building-2-fill text-white text-xl"></i>
                </div>
                <h3
                  className="text-2xl lg:text-3xl font-bold"
                  style={{ color: '#0A2540' }}
                >
                  会社基本情報
                </h3>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="w-32 text-sm font-semibold text-gray-500 mt-1">
                      商号
                    </div>
                    <div className="flex-1 font-medium text-gray-900">
                      株式会社クロポ
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-32 text-sm font-semibold text-gray-500 mt-1">
                      英文商号
                    </div>
                    <div className="flex-1 font-medium text-gray-900">
                      XROPO Inc.
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-32 text-sm font-semibold text-gray-500 mt-1">
                      設立年月日
                    </div>
                    <div className="flex-1 font-medium text-gray-900">
                      2025年9月29日
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-32 text-sm font-semibold text-gray-500 mt-1">
                      資本金
                    </div>
                    <div className="flex-1 font-medium text-gray-900">
                      200万円
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="w-32 text-sm font-semibold text-gray-500 mt-1">
                      代表取締役
                    </div>
                    <div className="flex-1 font-medium text-gray-900">
                      赤羽根 啓輔
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-32 text-sm font-semibold text-gray-500 mt-1">
                      事業年度
                    </div>
                    <div className="flex-1 font-medium text-gray-900">
                      9月1日〜8月31日
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-32 text-sm font-semibold text-gray-500 mt-1">
                      決算期
                    </div>
                    <div className="flex-1 font-medium text-gray-900">
                      8月31日
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-32 text-sm font-semibold text-gray-500 mt-1">
                      事業内容
                    </div>
                    <div className="flex-1 font-medium text-gray-900">
                      スポーツDXソリューション事業
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-4 bg-white rounded-lg border-l-4" style={{ borderColor: '#FFB800' }}>
                <p className="text-sm text-gray-600">
                  <i className="ri-information-line mr-2" style={{ color: '#FFB800' }}></i>
                  本ページの決算公告は、会社法第440条に基づく電子公告として掲載しています。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* お問い合わせ */}
      <section id="contact" className="py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2
              className="text-3xl lg:text-4xl font-bold mb-4"
              style={{ color: '#0A2540' }}
            >
              お問い合わせ
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              XROPOに関するご質問やご相談がございましたら、お気軽にお問い合わせください
            </p>
          </div>

          <div className="max-w-2xl mx-auto">
            <form
              id="contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-gray-50 p-8 rounded-2xl shadow-lg"
            >
              <div className="space-y-6">
                <div>
                  <label
                    htmlFor="type"
                    className="block text-sm font-semibold mb-2"
                    style={{ color: '#0A2540' }}
                  >
                    お問い合わせ種別 <span className="text-red-5">*</span>
                  </label>
                  <select
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                  >
                    <option value="">選択してください</option>
                    <option value="導入相談">導入相談</option>
                    <option value="技術的な質問">技術的な質問</option>
                    <option value="料金について">料金について</option>
                    <option value="その他">その他</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="company"
                    className="block text-sm font-semibold mb-2"
                    style={{ color: '#0A2540' }}
                  >
                    会社名・団体名
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-semibold mb-2"
                    style={{ color: '#0A2540' }}
                  >
                    お名前 <span className="text-red-5">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-semibold mb-2"
                    style={{ color: '#0A2540' }}
                  >
                    E-mail <span className="text-red-5">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-semibold mb-2"
                    style={{ color: '#0A2540' }}
                  >
                    電話番号
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus;border-transparent text-sm"
                  />
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-semibold mb-2"
                    style={{ color: '#0A2540' }}
                  >
                    お問い合わせ内容 <span className="text-red-5">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  ></textarea>
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full py-4 px-6 rounded-lg font-semibold text-lg transition-colors ${isSubmitting
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-yellow-400 hover:bg-yellow-500 text-gray-900'
                      }`}
                  >
                    {isSubmitting ? '送信中...' : '送信する'}
                  </button>
                </div>
              </div>

              {submitStatus && (
                <div
                  className={`mt-6 p-4 rounded-lg text-center ${
                    submitSuccess ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}
                >
                  {submitStatus}
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      <section id="privacy" className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold mb-8 text-center">プライバシーポリシー</h2>

          <div className="space-y-6 text-gray-700 leading-relaxed">

            <p>
              株式会社クロポ（以下「当社」）は、当社が提供するサービスにおける
              個人情報の取扱いについて、以下の通りプライバシーポリシーを定めます。
            </p>

            <div>
              <h3 className="font-bold mb-2">1. 取得する情報</h3>
              <p>
                当社は、お問い合わせフォームやサービス提供にあたり、
                氏名、メールアドレス、会社名、その他入力された情報を取得する場合があります。
              </p>
            </div>

            <div>
              <h3 className="font-bold mb-2">2. 利用目的</h3>
              <p>
                取得した個人情報は、以下の目的の範囲内で利用します。
              </p>
              <ul className="list-disc ml-6 mt-2">
                <li>お問い合わせへの対応</li>
                <li>サービス提供およびサポート</li>
                <li>サービス改善および品質向上</li>
                <li>必要に応じたご連絡</li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold mb-2">3. 第三者提供</h3>
              <p>
                当社は、法令に基づく場合を除き、
                ご本人の同意なく第三者に個人情報を提供することはありません。
              </p>
            </div>

            <div>
              <h3 className="font-bold mb-2">4. 安全管理</h3>
              <p>
                当社は、個人情報の漏えい、滅失または毀損の防止その他の安全管理のため、
                必要かつ適切な措置を講じます。
              </p>
            </div>

            <div>
              <h3 className="font-bold mb-2">5. 開示・訂正・削除</h3>
              <p>
                ご本人から個人情報の開示、訂正、削除等の請求があった場合、
                法令に基づき適切に対応いたします。
              </p>
            </div>

            <div>
              <h3 className="font-bold mb-2">6. アクセス解析ツールについて</h3>
              <p>
                当社は、サービス向上および利用状況の分析のため、
                Googleによるアクセス解析ツール「Google Analytics」を利用しています。
              </p>
              <p className="mt-2">
                Google Analyticsは、Cookieを使用して利用者の情報を収集します。
                収集される情報は匿名であり、個人を特定するものではありません。
              </p>
              <p className="mt-2">
                これらの情報は、Google社のプライバシーポリシーに基づいて管理されます。
                Google Analyticsの利用に関して、詳しい情報はこちら
                <a href="https://policies.google.com/technologies/partner-sites?hl=ja" target="_blank" rel="noopener noreferrer">
                  https://policies.google.com/technologies/partner-sites?hl=ja
                </a>
                をご覧ください。
              </p>
            </div>

            <div>
              <h3 className="font-bold mb-2">7. お問い合わせ窓口</h3>
              <p>
                個人情報に関するお問い合わせは、下記窓口までお願いいたします。
              </p>
              <p className="mt-2">
                株式会社クロポ<br />
                〒101-0041<br />
                東京都千代田区神田須田町1丁目7番8号　VORT秋葉原Ⅳ 2F<br />
                メール：info@xropo.jp
              </p>
            </div>

            <div>
              <h3 className="font-bold mb-2">8. 改定</h3>
              <p>
                本ポリシーは、法令の変更や必要に応じて改定される場合があります。
              </p>
            </div>

            <p className="text-sm text-gray-500 mt-6">
              制定日：2025年9月29日
            </p>

          </div>
        </div>
      </section>

      {/* フッター */}
      <footer className="py-20" style={{ backgroundColor: '#0A2540' }}>
        <div className="container mx-auto px-4 sm:px-6 text-white">
          <div className="grid md:grid-cols-3 gap-12">
            {/* 左側：ロゴ＋ミッション */}
            <div>
              <img
                src={logoWhiteImage}
                alt="XROPOロゴ"
                className="h-12 w-auto"
              />
              <p className="text-gray-300 leading-relaxed">
                スポーツの力で、人と社会に笑顔とつながりを。
              </p>
            </div>

            {/* 中央：クイックナビゲーション */}
            <div>
              <h4 className="text-lg font-bold mb-6">サイトマップ</h4>
              <ul className="space-y-3">
                <li><a href="#about" className="text-gray-300 hover:text-white transition-colors cursor-pointer">会社概要</a></li>
                <li><a href="#service" className="text-gray-300 hover:text-white transition-colors cursor-pointer">サービス紹介</a></li>
                <li><a href="#news" className="text-gray-300 hover:text-white transition-colors cursor-pointer">ニュース / プレスリリース</a></li>
                <li><a href="#" className="text-gray-300 hover:text-white transition-colors cursor-pointer">採用情報</a></li>
              </ul>
            </div>

            {/* 右側：ポリシー系 */}
            <div>
              <h4 className="text-lg font-bold mb-6">ポリシー・お問い合わせ</h4>
              <ul className="space-y-3">
                <li><a href="#privacy" className="text-gray-300 hover:text-white transition-colors cursor-pointer">プライバシーポリシー</a></li>
                {/* <li><a href="#" className="text-gray-300 hover:text-white transition-colors cursor-pointer">利用規約</a></li> */}
                <li><a href="#contact" className="text-gray-300 hover:text-white transition-colors cursor-pointer">お問い合わせ</a></li>
              </ul>
            </div>
          </div>

          {/* コピーライト */}
          <div className="border-t border-gray-700 mt-16 pt-8 text-center">
            <p className="text-gray-400">© 2025 XROPO Inc.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
